import React from 'react';
import styled from 'styled-components';

interface ToggleOption {
  value: string;
  label: string;
}

interface ToggleProps {
  options: ToggleOption[];
  value?: string;
  onChange: (value: string) => void;
  variant?: 'primary' | 'buy-sell';
  fullWidth?: boolean;
  disabled?: boolean;
  size?: 'small' | 'medium' | 'large';
}

interface ToggleOptionProps {
  option: ToggleOption;
  isSelected: boolean;
  onClick: () => void;
  variant: 'primary' | 'buy-sell';
  disabled?: boolean;
  size?: 'small' | 'medium' | 'large';
  $optionValue?: string; // For buy-sell variant (transient prop)
}

const getVariantStyles = (theme: any, variant: 'primary' | 'buy-sell', optionValue?: string) => {
  if (variant === 'buy-sell') {
    const isBuy = optionValue?.toLowerCase().includes('buy');
    return isBuy
      ? `
        background-color: ${theme.colors.success.light};
        color: ${theme.colors.success.dark};
        border-color: ${theme.colors.success.main};
        &:hover {
          background-color: ${theme.colors.success.main};
          color: white;
        }
      `
      : `
        background-color: ${theme.colors.error.light};
        color: ${theme.colors.error.dark};
        border-color: ${theme.colors.error.main};
        &:hover {
          background-color: ${theme.colors.error.main};
          color: white;
        }
      `;
  }
  return `
    background-color: ${theme.colors.primary.light};
    color: ${theme.colors.primary.dark};
    border-color: ${theme.colors.primary.main};
    &:hover {
      background-color: ${theme.colors.primary.main};
      color: white;
    }
  `;
};

const getSizeStyles = (size: 'small' | 'medium' | 'large') => {
  switch (size) {
    case 'small':
      return 'padding: 0.25rem 0.5rem; font-size: 0.75rem;';
    case 'large':
      return 'padding: 0.75rem 1.5rem; font-size: 1rem;';
    default:
      return 'padding: 0.5rem 1rem; font-size: 0.875rem;';
  }
};

const ToggleContainer = styled.div<{ $fullWidth?: boolean }>`
  display: flex;
  border-radius: ${({ theme }) => theme.shape.borderRadius.medium};
  overflow: hidden;
  width: ${({ $fullWidth }) => ($fullWidth ? '100%' : 'auto')};
`;

const ToggleButton = styled.button<{ 
  $isSelected: boolean; 
  variant: 'primary' | 'buy-sell'; 
  $optionValue?: string;
  size: 'small' | 'medium' | 'large';
}>`
  flex: 1;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background-color: ${({ theme }) => theme.colors.background.paper};
  color: ${({ theme }) => theme.colors.text.primary};
  ${({ size }) => getSizeStyles(size)}
  transition: all 0.2s ease;
  cursor: pointer;

  &:first-child {
    border-right: none;
  }

  &:last-child {
    border-left: none;
  }

  &:hover {
    background-color: ${({ theme }) => theme.colors.background.secondary};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  ${({ $isSelected, theme, variant, $optionValue }) =>
    $isSelected && getVariantStyles(theme, variant, $optionValue)}
`;

const ToggleOptionComponent: React.FC<ToggleOptionProps> = ({
  option,
  isSelected,
  onClick,
  variant,
  disabled,
  size = 'medium',
}) => (
  <ToggleButton
    $isSelected={isSelected}
    variant={variant}
    $optionValue={option.value}
    size={size}
    onClick={onClick}
    disabled={disabled}
  >
    {option.label}
  </ToggleButton>
);

export const Toggle: React.FC<ToggleProps> = ({
  options,
  value,
  onChange,
  variant = 'primary',
  fullWidth = false,
  disabled = false,
  size = 'medium',
}) => {
  return (
    <ToggleContainer $fullWidth={fullWidth}>
      {options.map((option) => (
        <ToggleOptionComponent
          key={option.value}
          option={option}
          isSelected={value === option.value}
          onClick={() => onChange(option.value)}
          variant={variant}
          disabled={disabled}
          size={size}
        />
      ))}
    </ToggleContainer>
  );
}; 